<script setup>
// Player de áudio de mensagem — o mesmo no inbox de cliente, na busca e no
// chat interno. Redesenhado em 15/09/2026 ("bem ruim e feio", Victor): antes
// esticava na largura toda, com botões de 20 e 28px e texto de 10px cravado.
//
// Quem usa pinta o tom do botão e das barras tocadas por `--audio-tom` (o chat
// interno usa o violeta dele). A variável tem outro nome porque é definida no
// MESMO elemento raiz: se fosse `--aud-tom`, a regra do pai e a daqui brigariam
// pela ordem das folhas.
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { iniciaisDoNome } from 'dashboard/helper/avatar';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, isOutgoing, showTranscribedText, avatarUrl, avatarNome } =
  defineProps({
    attachment: {
      type: Object,
      required: true,
    },
    isOutgoing: {
      type: Boolean,
      default: false,
    },
    showTranscribedText: {
      type: Boolean,
      default: true,
    },
    // O rosto de quem gravou, com o selo de microfone — é o que faz um áudio
    // parecer um áudio numa lista de mensagens, e não um anexo. Vazio, o
    // player continua inteiro: o avatar simplesmente não entra.
    avatarUrl: {
      type: String,
      default: '',
    },
    avatarNome: {
      type: String,
      default: '',
    },
  });

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const audioMimeHint = computed(() => {
  const rawType = (attachment.contentType || attachment.content_type || '')
    .toString()
    .trim()
    .toLowerCase();
  if (rawType === 'audio/opus') return 'audio/ogg; codecs=opus';
  if (rawType) return rawType;

  const ext = (attachment.extension || '').toString().toLowerCase();
  if (['ogg', 'oga', 'opus'].includes(ext)) return 'audio/ogg; codecs=opus';
  if (ext === 'mp3') return 'audio/mpeg';
  if (ext === 'm4a' || ext === 'mp4') return 'audio/mp4';
  if (ext === 'wav') return 'audio/wav';
  return '';
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

// Static waveform bars — 38 bars, heights 4–24 px, deterministic pattern
const waveformBars = (() => {
  const bars = [];
  for (let i = 0; i < 38; i += 1) {
    const base = Math.sin(i * 0.4) * 6 + Math.sin(i * 1.1) * 4;
    bars.push(
      Math.max(
        4,
        Math.min(24, Math.round(12 + base + (((i * 7 + 3) % 11) - 5)))
      )
    );
  }
  return bars;
})();

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
};

// "1,0×", "1,5×", "2,0×" — o rótulo dos mensageiros, com vírgula decimal e o
// sinal de multiplicação, e SEMPRE com uma casa: "1x" e "1.5x" tinham larguras
// diferentes e a pílula mudava de tamanho a cada toque.
const playbackSpeedLabel = computed(
  () =>
    `${playbackSpeed.value.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}×`
);

const fotoQuebrada = ref(false);
const temRosto = computed(() => Boolean(avatarUrl || avatarNome));

// Sem rosto para mostrar, a velocidade fica sempre à mão; com rosto, ela
// aparece enquanto toca — e continua enquanto não estiver em 1x, para quem
// volta ao áudio ver que ele não está no normal.
const mostraVelocidade = computed(
  () => !temRosto.value || isPlaying.value || playbackSpeed.value !== 1
);

onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

// O rótulo do tempo segue a convenção dos mensageiros: parado no começo, ele
// mostra QUANTO DURA (é o que se quer saber antes de apertar o play); tocando
// ou parado no meio, mostra ONDE ESTÁ. "0:03 / 1:12" faz a pessoa ler duas
// contas para responder uma pergunta.
const timeLabel = computed(() =>
  currentTime.value > 0
    ? formatTime(currentTime.value)
    : formatTime(duration.value)
);

// Para o leitor de tela o par completo continua valendo — ali não há espaço
// para inferir o contexto pelo desenho.
const timeLabelCompleto = computed(
  () => `${formatTime(currentTime.value)} / ${formatTime(duration.value)}`
);

const progressRatio = computed(() => {
  return duration.value ? currentTime.value / duration.value : 0;
});

const isBarPlayed = index => {
  return index / waveformBars.length <= progressRatio.value;
};

const seekTo = time => {
  const limite = Math.max(0, Math.min(duration.value || 0, time));
  audioPlayer.value.currentTime = limite;
  currentTime.value = limite;
};

// 🔴 A ONDA SE ARRASTA, e não só se clica (16/09/2026).
//
// Quem ouve áudio de cliente volta trechos o tempo todo — "repete o nome da
// empresa", "qual era a data". Com clique isolado é tentativa e erro: some o
// dedo, ouve, erra, clica de novo. Arrastando, a pessoa PROCURA o ponto.
// `setPointerCapture` mantém o gesto vivo mesmo saindo da barra, que é o que
// o dedo faz no celular.
const arrastando = ref(false);

const arrastarAte = event => {
  const barra = event.currentTarget;
  const rect = barra.getBoundingClientRect();
  const ratio = Math.max(
    0,
    Math.min(1, (event.clientX - rect.left) / rect.width)
  );
  seekTo(ratio * (duration.value || 0));
};

const aoPegar = event => {
  arrastando.value = true;
  event.currentTarget.setPointerCapture?.(event.pointerId);
  arrastarAte(event);
};

const aoMover = event => {
  if (!arrastando.value) return;
  arrastarAte(event);
};

const aoSoltar = event => {
  if (!arrastando.value) return;
  arrastando.value = false;
  event.currentTarget.releasePointerCapture?.(event.pointerId);
};

// A onda é um controle deslizante: sem teclado, só quem tem mouse avançava.
const PASSO_TECLADO = 5;
const seekFromKeyboard = event => {
  const destinos = {
    ArrowRight: currentTime.value + PASSO_TECLADO,
    ArrowUp: currentTime.value + PASSO_TECLADO,
    ArrowLeft: currentTime.value - PASSO_TECLADO,
    ArrowDown: currentTime.value - PASSO_TECLADO,
    Home: 0,
    End: duration.value || 0,
  };
  if (!(event.key in destinos)) return;
  event.preventDefault();
  seekTo(destinos[event.key]);
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const nextIndex = (speeds.indexOf(playbackSpeed.value) + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <!-- `play`/`pause` do próprio elemento mandam no botão: o áudio também
       para pela tecla de mídia do teclado, pelo fone Bluetooth ou pelo
       controle do sistema, e aí o botão ficava mostrando "pausar" com o áudio
       parado (medido na tela em 16/09/2026). -->
  <audio
    ref="audioPlayer"
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @play="isPlaying = true"
    @pause="isPlaying = false"
    @ended="onEnd"
  >
    <source :src="timeStampURL" :type="audioMimeHint || undefined" />
  </audio>

  <div
    v-bind="$attrs"
    class="aud"
    :class="{
      'aud--saida': isOutgoing,
      'aud--tocando': isPlaying,
      'aud--arrastando': arrastando,
    }"
  >
    <!-- O rosto de quem gravou, com o selo de microfone. Fica do lado de FORA
         da linha: na mensagem que chega, à direita; na que sai, à esquerda —
         a mesma leitura dos mensageiros, onde o avatar fica sempre do lado de
         quem falou. -->
    <!-- A PONTA do player: parado, é o rosto de quem gravou; tocando (ou fora
         de 1x), vira o botão de velocidade no MESMO lugar — como nos
         mensageiros. Assim a velocidade aparece quando serve e o rosto não
         disputa espaço com ela. -->
    <div class="aud__ponta">
      <Transition name="aud-troca" mode="out-in">
        <button
          v-if="mostraVelocidade"
          key="vel"
          v-tooltip.top="
            $t('CONVERSATION.AUDIO_PLAYER.SPEED', { speed: playbackSpeedLabel })
          "
          type="button"
          class="aud__vel"
          :class="{ 'aud__vel--on': playbackSpeed !== 1 }"
          :aria-label="
            $t('CONVERSATION.AUDIO_PLAYER.SPEED', { speed: playbackSpeedLabel })
          "
          @click="changePlaybackSpeed"
        >
          {{ playbackSpeedLabel }}
        </button>
        <span v-else key="quem" class="aud__quem" aria-hidden="true">
          <img
            v-if="avatarUrl && !fotoQuebrada"
            :src="avatarUrl"
            alt=""
            class="aud__quem-foto"
            @error="fotoQuebrada = true"
          />
          <span v-else class="aud__quem-iniciais">
            {{ iniciaisDoNome(avatarNome) }}
          </span>
          <span class="aud__quem-selo">
            <Icon icon="i-ph-microphone-fill" class="aud__quem-ico" />
          </span>
        </span>
      </Transition>
    </div>

    <button
      type="button"
      class="aud__play"
      :aria-label="
        isPlaying
          ? $t('CONVERSATION.AUDIO_PLAYER.PAUSE')
          : $t('CONVERSATION.AUDIO_PLAYER.PLAY')
      "
      @click="playOrPause"
    >
      <Icon
        :icon="isPlaying ? 'i-ph-pause-fill' : 'i-ph-play-fill'"
        class="aud__play-ico"
        :class="{ 'aud__play-ico--play': !isPlaying }"
      />
    </button>

    <div class="aud__corpo">
      <div
        class="aud__onda"
        role="slider"
        tabindex="0"
        :aria-label="$t('CONVERSATION.AUDIO_PLAYER.POSITION')"
        aria-valuemin="0"
        :aria-valuemax="Math.round(duration || 0)"
        :aria-valuenow="Math.round(currentTime || 0)"
        :aria-valuetext="timeLabelCompleto"
        @pointerdown="aoPegar"
        @pointermove="aoMover"
        @pointerup="aoSoltar"
        @pointercancel="aoSoltar"
        @keydown="seekFromKeyboard"
      >
        <span
          v-for="(h, i) in waveformBars"
          :key="i"
          class="aud__barra"
          :class="{ 'aud__barra--tocada': isBarPlayed(i) }"
          :style="{ height: `${h}px` }"
        />
        <!-- A bolinha marca onde o áudio está. É o que transforma a onda de
             desenho em CONTROLE: sem ela, a barra tocada some no meio das
             outras e não há o que pegar com o dedo. -->
        <span
          class="aud__bolinha"
          :style="{ insetInlineStart: `${progressRatio * 100}%` }"
          aria-hidden="true"
        />
      </div>
      <div class="aud__rodape">
        <span class="aud__tempo">{{ timeLabel }}</span>
        <!-- Baixar mora no rodapé, pequeno, ao lado do tempo: é ação rara
             para ocupar lugar na linha principal, e esconder só no hover
             deixaria quem usa celular sem ela. -->
        <button
          v-tooltip.top="$t('CONVERSATION.DOWNLOAD')"
          type="button"
          class="aud__baixar"
          :aria-label="$t('CONVERSATION.DOWNLOAD')"
          @click="downloadAudio"
        >
          <Icon icon="i-ph-download-simple" class="aud__baixar-ico" />
        </button>
        <span class="aud__meta"><slot name="meta" /></span>
      </div>
    </div>
  </div>

  <div
    v-if="
      (attachment.transcribedText || attachment.transcribed_text) &&
      showTranscribedText
    "
    class="aud-transcricao"
    :class="{ 'aud-transcricao--saida': isOutgoing }"
  >
    {{ attachment.transcribedText || attachment.transcribed_text }}
  </div>
</template>

<style scoped>
.aud {
  --aud-tom: var(--audio-tom, rgb(var(--blue-9)));
  --aud-tom-tinta: rgb(255 255 255);
  --aud-trilho: rgb(var(--slate-7));
  --aud-tinta: rgb(var(--slate-11));
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  /* Largura PRÓPRIA, e não a da bolha: a bolha se mede pelo conteúdo, então
     `min(300px, 100%)` virava um círculo — o player media 203px e a onda,
     81px, pequena demais para o dedo achar o trecho. 320px dá à onda uns
     200px; `70vw` segura o celular (273px numa tela de 390). */
  width: min(320px, 70vw);
  max-width: 100%;
  /* O recuo de baixo guarda o lugar do rodapé, que pende da onda. */
  padding: var(--esp-2) var(--esp-3) var(--esp-5) var(--esp-2);
}

/* Na bolha de saída do inbox o fundo já é a cor: o botão vira branco e a
   seta toma a cor da bolha. */
.aud--saida {
  --aud-tom: rgb(255 255 255);
  --aud-tom-tinta: rgb(var(--blue-10));
  --aud-trilho: rgb(255 255 255 / 0.35);
  --aud-tinta: rgb(255 255 255 / 0.8);
}

.aud__play {
  position: relative;
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: var(--aud-tom);
  color: var(--aud-tom-tinta);
  box-shadow: 0 4px 12px -4px color-mix(in srgb, var(--aud-tom) 60%, transparent);
  cursor: pointer;
  transition:
    scale var(--mov-2) var(--mov-curva-pulo),
    box-shadow var(--mov-2) var(--mov-curva);
}

.aud__play:hover {
  scale: 1.06;
}

.aud__play:active {
  scale: 0.94;
}

/* Tocando, o botão ganha um anel parado da própria cor — diz "este é o que
   está tocando" sem pulsar. */
.aud--tocando .aud__play {
  box-shadow:
    0 0 0 4px color-mix(in srgb, var(--aud-tom) 20%, transparent),
    0 4px 12px -4px color-mix(in srgb, var(--aud-tom) 60%, transparent);
}

.aud__play-ico {
  width: 18px;
  height: 18px;
}

/* O triângulo do play tem o peso à esquerda: no centro exato parece torto. */
.aud__play-ico--play {
  translate: 1px 0;
}

/* ── O rosto de quem gravou ───────────────────────────────────────────────
   Entra DEPOIS do corpo na ordem natural (mensagem que chega: avatar à
   direita) e é reordenado na mensagem que sai, onde ele vem primeiro. */
/* A ponta tem largura FIXA: rosto e velocidade trocam de lugar nela, e a
   linha não pode pular quando um sai e o outro entra. */
.aud__ponta {
  display: grid;
  order: 3;
  place-items: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
}

.aud--saida .aud__ponta {
  order: 0;
}

.aud-troca-enter-active,
.aud-troca-leave-active {
  transition:
    opacity var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo);
}

.aud-troca-enter-from,
.aud-troca-leave-to {
  opacity: 0;
  scale: 0.7;
}

.aud__quem {
  position: relative;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: var(--r-redondo);
  background: linear-gradient(140deg, rgb(15 23 42), var(--aud-tom) 135%);
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
  color: rgb(255 255 255);
}

.aud__quem-foto {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

/* O selo de microfone é o que diz "isto é um áudio" antes de qualquer
   leitura — o mesmo papel do ícone no canto do avatar nos mensageiros. */
.aud__quem-selo {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border: 2px solid var(--aud-fundo, rgb(var(--solid-1)));
  border-radius: var(--r-redondo);
  background: var(--aud-tom);
  color: var(--aud-tom-tinta);
}

.aud__quem-ico {
  width: 9px;
  height: 9px;
}

/* 🔴 A ONDA ALINHA COM O PLAY (Victor, 16/09/2026: "as ondinhas estão mais
   para cima, deixa centralizado com o botão de play").
   Onda e rodapé empilhados faziam o CONJUNTO centrar com o botão — e a onda,
   que é a metade de cima, ficava acima do centro. Agora o corpo mede só a
   onda, e o rodapé pendura por baixo dela (fora da conta do alinhamento),
   como nos mensageiros. O player reserva a altura do rodapé no recuo de
   baixo. */
.aud__corpo {
  position: relative;
  display: flex;
  flex: 1;
  order: 2;
  flex-direction: column;
  min-width: 0;
}

.aud__play {
  order: 1;
}

.aud__onda {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 28px;
  border-radius: var(--r-1);
  cursor: pointer;
  touch-action: none;
}

.aud__onda:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.aud__barra {
  flex: 0 1 3px;
  min-width: 2px;
  border-radius: var(--r-redondo);
  background: var(--aud-trilho);
  transition: background-color var(--mov-1) var(--mov-curva);
}

.aud__barra--tocada {
  background: var(--aud-tom);
}

/* A bolinha do ponto atual. `translate` de -50% para ela ficar CENTRADA no
   ponto, e não começando nele. */
.aud__bolinha {
  position: absolute;
  top: 50%;
  width: 11px;
  height: 11px;
  border-radius: var(--r-redondo);
  background: var(--aud-tom);
  box-shadow: 0 1px 4px rgb(15 23 42 / 35%);
  translate: -50% -50%;
  transition: scale var(--mov-1) var(--mov-curva-pulo);
  pointer-events: none;
}

.aud--saida .aud__bolinha {
  box-shadow: 0 1px 4px rgb(15 23 42 / 25%);
}

/* Enquanto o dedo arrasta, a bolinha cresce: é o retorno de que o gesto
   pegou — sem isso, arrastar no celular parece não ter acontecido. */
.aud--arrastando .aud__bolinha,
.aud__onda:hover .aud__bolinha {
  scale: 1.25;
}

.aud__rodape {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  height: 18px;
}

/* A hora e o status vão para a ponta direita, como nos mensageiros. */
.aud__meta {
  display: inline-flex;
  margin-inline-start: auto;
}

.aud__tempo {
  font-size: var(--txt-selo);
  font-weight: var(--peso-dado);
  line-height: var(--linha-apertada);
  color: var(--aud-tinta);
  font-variant-numeric: tabular-nums;
}

.aud__vel,
.aud__baixar {
  position: relative;
  display: inline-grid;
  flex-shrink: 0;
  place-items: center;
  padding: 0;
  border: 0;
  color: var(--aud-tinta);
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

/* A pílula de velocidade é sólida, como nos mensageiros: ela aparece no lugar
   do rosto no instante do play e precisa ser vista sem procurar. */
.aud__vel {
  min-width: 42px;
  height: 24px;
  padding: 0 var(--esp-2);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-11));
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
  line-height: 1;
  color: rgb(var(--solid-1));
  font-variant-numeric: tabular-nums;
}

.aud__vel:hover {
  background: rgb(var(--slate-12));
}

/* Fora de 1,0× a pílula toma a cor do tom: quem volta ao áudio vê que ele
   não está no normal. */
.aud__vel--on {
  background: var(--aud-tom);
  color: var(--aud-tom-tinta);
}

.aud--saida .aud__vel {
  background: rgb(255 255 255 / 25%);
  color: rgb(255 255 255);
}

.aud--saida .aud__vel--on {
  background: rgb(255 255 255);
  color: rgb(var(--blue-10));
}

/* Pequeno de propósito: está no rodapé, ao lado do tempo, não na linha
   principal. No celular o alvo cresce por fora (regra do fim do arquivo). */
.aud__baixar {
  width: 20px;
  height: 20px;
  border-radius: var(--r-1);
  background: transparent;
  opacity: 0.75;
}

.aud__baixar:hover {
  opacity: 1;
}

.aud__baixar:hover {
  background: color-mix(in srgb, var(--aud-tinta) 14%, transparent);
}

.aud__baixar-ico {
  width: var(--ico-1);
  height: var(--ico-1);
}

.aud__play:focus-visible,
.aud__vel:focus-visible,
.aud__baixar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

/* No celular o desenho segue e o alvo cresce por fora até 44. */
@media (max-width: 639px) {
  .aud__play::after,
  .aud__vel::after,
  .aud__baixar::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  .aud__vel::after {
    inset: -10px -4px;
  }
}

.aud-transcricao {
  width: 100%;
  margin-top: var(--esp-1);
  padding: var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--slate-9) / 6%);
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-12));
  overflow-wrap: anywhere;
}

/* Na bolha azul de saída, o texto cinza-escuro quase sumia (Victor,
   17/09/2026): branco, sobre um vidro claro. */
.aud-transcricao--saida {
  background: rgb(255 255 255 / 10%);
  color: rgb(255 255 255);
}

@media (prefers-reduced-motion: reduce) {
  .aud__play,
  .aud__barra,
  .aud__bolinha,
  .aud__vel,
  .aud__baixar,
  .aud-troca-enter-active,
  .aud-troca-leave-active {
    transition: none;
  }
}
</style>
